import React, { useState } from 'react';
import { ToolbarButton } from '@/components/creator/partials/toolbarButton/ToolbarButton';
import { useMediaQuery } from '@mantine/hooks';
import { Flex, Popover, Text } from '@mantine/core';
import { ProjectRenderer } from '@/renderer/ProjectRenderer';
import { useTypedDispatch, useTypedSelector } from '@/store/hooks';
import { creatorActions } from '@/store/slices/creatorSlice';
import {
	IconArrowBackUp,
	IconAugmentedReality,
	IconFocusCentered,
	IconRulerMeasure,
	IconZoomIn,
	IconZoomOut,
} from '@tabler/icons-react';
import { ModelViewerElement } from '@google/model-viewer';
import { getFileSrc } from '@/utils/utilities';
import { useMutation } from '@tanstack/react-query';
import MeasureIcon from '@/assets/icons/measure.svg?react';
import CenterIcon from '@/assets/icons/centerRoom.svg?react';
import MaximizeIcon from '@/assets/icons/maximize.svg?react';
import MinimizeIcon from '@/assets/icons/minimize.svg?react';
import APP_CONFIG from '@/configs/appConfig';
import QRCode from 'react-qr-code';
import styles from './Toolbar.module.scss';
import AppApi from '@/api/AppApi';

const baseSlug = 'toolbar.';

const modelViewer = document.querySelector(
	'model-viewer'
) as ModelViewerElement;

const renderer = ProjectRenderer.getInstance();

interface Props {
	zoomEnabled: {
		in: boolean;
		out: boolean;
	};
	handleZoom: (zoomAction: 'in' | 'out') => () => void;
	toggleFullscreen: () => Promise<void>;
	fullscreen: boolean;
	handleCenterView: () => void;
}

export const Toolbar: React.FC<Props> = ({
	zoomEnabled,
	handleZoom,
	toggleFullscreen,
	fullscreen,
	handleCenterView,
}) => {
	const dispatch = useTypedDispatch();

	const { options, snapshots, selectedProductModuleCollection } =
		useTypedSelector((state) => state.creator);

	const isMobile = useMediaQuery(`(max-width: ${APP_CONFIG.BREAKPOINTS.MD})`);

	const [controller, setController] = useState<AbortController | null>(null);
	const clearController = () => {
		controller?.abort();
		setController(null);
	};

	const [previewUrl, setPreviewUrl] = useState('');
	const clearPreviewUrl = () => {
		setPreviewUrl('');
		clearController();
	};

	const handleAR = async () => {
		dispatch(creatorActions.updateOptions({ isLoading: true }));

		const glb = await renderer.saveModel();
		const attachment = await AppApi.createARModel(glb);

		if (!modelViewer) return;

		const newController = new AbortController();
		setController(newController);
		const signal = newController.signal;

		modelViewer.setAttribute('src', getFileSrc(attachment.path));
		modelViewer.addEventListener(
			'load',
			({ detail }: any) => {
				dispatch(creatorActions.updateOptions({ isLoading: false }));

				if (!modelViewer.canActivateAR) {
					setPreviewUrl(`${import.meta.env.VITE_AR_URL}?model=${detail.url}`);
					return;
				}

				modelViewer.activateAR();
			},
			{
				signal,
			}
		);

		return attachment;
	};

	const ARMutation = useMutation({
		mutationFn: handleAR,
		onSettled: (attachment) => {
			clearController();
		},
	});

	const handleARClick = () => {
		ARMutation.mutate();
	};

	const toggleMeasureVisibility = () => {
		dispatch(
			creatorActions.updateOptions({
				isMetersVisible: !options.isMetersVisible,
			})
		);

		renderer.toggleBoxMeasuresVisibility();
	};

	const undo = () => {
		dispatch(creatorActions.undo());
	};

	const buttons = isMobile ? (
		<>
			<ToolbarButton
				Icon={fullscreen ? MinimizeIcon : MaximizeIcon}
				onClick={toggleFullscreen}
			/>
			<ToolbarButton
				Icon={CenterIcon}
				onClick={handleCenterView}
				disabled={!selectedProductModuleCollection}
			/>
			<ToolbarButton
				Icon={MeasureIcon}
				onClick={toggleMeasureVisibility}
				disabled={!selectedProductModuleCollection}
			/>
			<ToolbarButton Icon={IconAugmentedReality} onClick={handleARClick} />
		</>
	) : (
		<>
			<ToolbarButton
				Icon={IconZoomIn}
				label="Przybliż"
				onClick={handleZoom('in')}
				disabled={!zoomEnabled.in || !selectedProductModuleCollection}
			/>
			<ToolbarButton
				Icon={IconZoomOut}
				label="Oddal"
				onClick={handleZoom('out')}
				disabled={!zoomEnabled.out || !selectedProductModuleCollection}
			/>
			<ToolbarButton
				Icon={IconFocusCentered}
				label="Wycentruj"
				onClick={handleCenterView}
				disabled={!selectedProductModuleCollection}
			/>
			<ToolbarButton
				Icon={IconRulerMeasure}
				label="Wymiary"
				onClick={toggleMeasureVisibility}
				disabled={!selectedProductModuleCollection}
			/>
			<ToolbarButton
				Icon={IconArrowBackUp}
				label="Cofnij"
				onClick={undo}
				disabled={!snapshots.length}
			/>
			<Popover position="top" opened={!!previewUrl} onChange={clearPreviewUrl}>
				<Popover.Target>
					<ToolbarButton
						Icon={IconAugmentedReality}
						label="AR"
						disabled={!selectedProductModuleCollection}
						onClick={handleAR}
					/>
				</Popover.Target>
				<Popover.Dropdown>
					{!!previewUrl && <QRCode value={previewUrl} size={128} />}
					<Text size="xs" maw={128} mt="xs">
						Zeskanuj kod QR, aby zobaczyć produkt w rzeczywistości rozszerzonej.
					</Text>
				</Popover.Dropdown>
			</Popover>
		</>
	);

	return (
		<Flex
			py={{
				base: 'xs',
				md: 'md',
			}}
			px={{ base: 'sm', md: 'xl' }}
			gap="xl"
			className={styles.container}
		>
			{buttons}
		</Flex>
	);
};
